import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { REGISTER } from "@/constants/testIds";
import { ArrowRight, GoogleLogo, Eye, EyeSlash } from "@phosphor-icons/react";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1566097127420-26750d93591e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHx2aW50YWdlJTIwZnVybml0dXJlJTIwZWRpdG9yaWFsfGVufDB8fHx8MTc4Mjg3MDgzMHww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { user, register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const registeredRef = useRef(false);

  useEffect(() => {
    if (user && !registeredRef.current) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    registeredRef.current = true;
    try {
      await register(email.trim(), password, name.trim());
      toast.success("Your account is ready — pick a plan to start your free trial");
      navigate("/plans", { replace: true });
    } catch (err) {
      registeredRef.current = false;
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create your account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]" data-testid="register-page">
      <div className="flex flex-col justify-center px-6 sm:px-14 py-12 max-w-xl w-full mx-auto vv-fade-up">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#E5E0D6]" />
          <span className="font-serif-display text-2xl font-bold tracking-tight">Vintage Vantage</span>
        </Link>

        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-2">Own your work.</h1>
        <p className="text-[17px] text-[#525252] leading-relaxed mb-8">Create your account and start building a portfolio no marketplace can lock away.</p>

        <button
          data-testid="register-google-button"
          onClick={googleLogin}
          className="w-full h-14 rounded-none border border-[#0D1B2A] font-medium text-[17px] inline-flex items-center justify-center gap-3 hover:bg-[#F5F3ED] transition-colors"
        >
          <GoogleLogo size={22} weight="bold" /> Continue with Google
        </button>

        <div className="flex items-center gap-4 my-6">
          <span className="h-px flex-1 bg-[#D8D3C8]" />
          <span className="label-caps">or with email</span>
          <span className="h-px flex-1 bg-[#D8D3C8]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-caps block mb-1.5">Your name</label>
            <input
              data-testid={REGISTER.nameInput}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Dealer"
              className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Email</label>
            <input
              data-testid={REGISTER.emailInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid={REGISTER.passwordInput}
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6+ characters"
                  className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 pr-12 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A867D] hover:text-[#0D1B2A]" aria-label="Toggle password">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Confirm</label>
              <input
                data-testid={REGISTER.passwordConfirmInput}
                type={showPw ? "text" : "password"}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat password"
                className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
              />
            </div>
          </div>
          <button
            data-testid={REGISTER.submitButton}
            type="submit"
            disabled={busy}
            className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50"
          >
            {busy ? "Creating your account…" : "Create account"} <ArrowRight size={18} weight="bold" />
          </button>
        </form>

        <p className="text-[13px] text-[#8A867D] mt-4" data-testid="register-terms-note">
          By creating an account you agree to our{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-[#B47A3C]">Terms of Service &amp; Privacy Policy</Link>.
        </p>

        <p className="text-[15px] text-[#525252] mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid={REGISTER.loginLink} className="font-semibold text-[#0D1B2A] underline underline-offset-4 hover:text-[#B47A3C]">
            Sign in
          </Link>
        </p>
      </div>

      <div className="hidden lg:block relative">
        <img src={AUTH_IMG} alt="Vintage furniture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0D1B2A]/25" />
        <div className="absolute bottom-10 left-10 right-10 text-[#FDFBF7]">
          <p className="font-serif-display text-3xl leading-tight font-medium max-w-md">
            "Import the work once. List it everywhere. Own it forever."
          </p>
        </div>
      </div>
    </div>
  );
}
